import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import healthcomService from '../../services/healthcom.service';
import Modal from '../common/Modal';
import mildPain from './images/mildPain.png';
import moderatePain from './images/moderatePain.png';
import noPain from './images/noPain.png';
import severePain from './images/severePain.png';
import verySeverePain from './images/verySeverePain.png';
import worstPain from './images/worstPain.png';

// Wong-Baker FACES style pain rating, 0–10 in steps of 2. Same
// values as the legacy app so existing dashboards / triage queries
// keep working without a migration.
type PainKey = 'noPain' | 'mild' | 'moderate' | 'severe' | 'verySevere' | 'worst';

const painIcons: { value: PainKey; icon: string; level: number }[] = [
  { value: 'noPain', icon: noPain, level: 0 },
  { value: 'mild', icon: mildPain, level: 2 },
  { value: 'moderate', icon: moderatePain, level: 4 },
  { value: 'severe', icon: severePain, level: 6 },
  { value: 'verySevere', icon: verySeverePain, level: 8 },
  { value: 'worst', icon: worstPain, level: 10 },
];

type Props = {
  open: boolean;
  setOpen: (val: boolean) => void;
  language: 'en' | 'es';
  primaryColor?: string;
  onSubmitted?: () => void;
};

const PainLevelSurvey: React.FC<Props> = ({
  open,
  setOpen,
  language,
  primaryColor = '#77bd43',
  onSubmitted,
}) => {
  const { refreshPatientData, selectedPatient } = usePatientPortal();
  const [selectedLevel, setSelectedLevel] = useState<number | undefined>();
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset state shortly after the modal closes so a re-open starts
  // fresh. The 200ms delay matches the dialog's exit animation so
  // the user doesn't see the form flash back to empty mid-close.
  useEffect(() => {
    if (open) return;
    const t = window.setTimeout(() => {
      setSelectedLevel(undefined);
      setCompleted(false);
      setSubmitting(false);
    }, 200);

    return () => window.clearTimeout(t);
  }, [open]);

  const submit = async () => {
    if (typeof selectedLevel !== 'number') return;
    if (!selectedPatient?.ID) return;

    setSubmitting(true);

    try {
      await healthcomService.painLevelSurvey({
        level: selectedLevel,
        patientId: selectedPatient.ID,
      });
      // Pull the latest patient data so `completedSurveys` reflects
      // today's entry — gates the CTAs on Home / ExerciseDetail and
      // hides them for the rest of the day.
      await refreshPatientData();
      setCompleted(true);
      onSubmitted?.();
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={open} close={() => setOpen(false)} size="default" showCloseButton>
      <div className="flex flex-col gap-5 sm:gap-6">
        <div className="flex flex-col items-center text-center gap-3">
          <div
            className="flex items-center justify-center size-12 rounded-full"
            style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
            aria-hidden="true"
          >
            <CheckCircleSolid className="size-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">
              {hcVerbiage.completeExercises[language]}
            </h3>
            {!completed && (
              <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400 max-w-prose">
                {hcVerbiage.pleaseRate[language]}
              </p>
            )}
          </div>
        </div>

        {completed ? (
          <p className="text-center text-sm text-gray-700 dark:text-zinc-200 py-2">
            {hcVerbiage.thankYou[language]}
          </p>
        ) : (
          <fieldset>
            <legend className="sr-only">{hcVerbiage.pleaseRate[language]}</legend>
            <div className="flex flex-col gap-2">
              {painIcons.map(opt => {
                const checked = selectedLevel === opt.level;

                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 cursor-pointer rounded-2xl px-3 py-2.5 transition-colors ${
                      checked
                        ? 'text-white'
                        : 'ring-1 ring-gray-950/10 dark:ring-white/10 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 hover:bg-gray-50 dark:hover:bg-zinc-700'
                    }`}
                    style={checked ? { backgroundColor: primaryColor } : undefined}
                  >
                    <input
                      type="radio"
                      name="painLevel"
                      value={opt.level}
                      checked={checked}
                      disabled={submitting}
                      onChange={() => setSelectedLevel(opt.level)}
                      className="sr-only"
                    />
                    <img
                      src={opt.icon}
                      alt=""
                      aria-hidden="true"
                      className="size-10 object-contain shrink-0"
                    />
                    <p className="flex-1 min-w-0 text-sm font-semibold">
                      {hcVerbiage[opt.value][language]}
                    </p>
                    {checked ? (
                      <CheckCircleSolid className="size-5 shrink-0" aria-hidden="true" />
                    ) : (
                      <span className="text-xs font-semibold tabular-nums text-gray-400 dark:text-zinc-500 shrink-0">
                        {opt.level}/10
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}

        <div className={`grid gap-3 ${completed ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-zinc-200 ring-1 ring-gray-950/10 dark:ring-white/10 hover:bg-gray-50 dark:hover:bg-zinc-700"
          >
            {hcVerbiage.close[language]}
          </button>

          {!completed && (
            <button
              type="button"
              onClick={submit}
              disabled={typeof selectedLevel !== 'number' || submitting}
              className="inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98] transition-transform"
              style={{ backgroundColor: primaryColor }}
            >
              {hcVerbiage.submit[language]}
              <CheckCircleIcon className="size-4" />
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PainLevelSurvey;
